import {useCallback} from 'react'

import {
  usePullsDashboardActions,
  usePullsDashboardCurrentPage,
  usePullsDashboardFetchedQuery,
  usePullsDashboardIsLoading,
} from '../contexts/PullsDashboardSurfaceStateContext'
import type {PullsDashboardStateCountSurfacePayload} from '../types/routes-types'
import {useAnalytics} from '@github-ui/use-analytics'
import {getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'

interface UsePullsDashboardStateType {
  isLoading: boolean
  results: PullsDashboardStateCountSurfacePayload
  currentPage: number
  methods: {
    fetchResults: (params: {q: string; page: number; sidebarLinkId?: string}) => void
    updateAndSubmitQuery: (q: string) => void
  }
}

/**
 * Hook to manage the state of the pulls dashboard page results.
 * Handles the display logic for showing empty results when the query is cleared.
 *
 * @param payload - The page payload containing pull request search results
 * @returns An object containing:
 *   - isLoading: Whether the data is currently loading
 *   - fetchedQuery: The current search query
 *   - results: The pull request search results (or empty results if query is cleared)
 *   - currentPage: The current page number
 *   - methods: Object with fetchResults and updateAndSubmitQuery functions
 */
export function usePullsDashboardState(payload: PullsDashboardStateCountSurfacePayload): UsePullsDashboardStateType {
  const fetchedQuery = usePullsDashboardFetchedQuery()
  const currentPage = usePullsDashboardCurrentPage()
  const isLoading = usePullsDashboardIsLoading()
  const {fetchResults} = usePullsDashboardActions()
  const {sendAnalyticsEvent} = useAnalytics()

  // When fetchedQuery is empty, the user has explicitly cleared the filter
  // In this case, show empty results to trigger the "Enter a query" message
  const emptyResults: PullsDashboardStateCountSurfacePayload = {
    results: [],
    pageInfo: {currentPage: 1, totalPages: 1, totalCount: 0, openCount: 0, closedCount: 0},
  }
  const hasQuery = !!fetchedQuery
  const results = hasQuery ? payload : emptyResults
  const updateAndSubmitQuery = useCallback(
    (q: string) => {
      sendAnalyticsEvent('pull-requests.search', 'UPDATE_AND_SUBMIT_QUERY', {sort: getSortFromQuery(q)})

      fetchResults({q, page: 1})
    },
    [fetchResults, sendAnalyticsEvent],
  )

  return {
    isLoading,
    results,
    currentPage,
    methods: {
      fetchResults,
      updateAndSubmitQuery,
    },
  }
}
